import { useHomeTabs } from '../../queries/useHomeTabs';
import { TabView } from 'react-native-tab-view';
import { type ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Typography, useScreenSize } from '@banhala/ui-kit';
import { useSetAtom } from 'jotai';
import {
  activeHomeTabAtom,
  type ActiveHomeTab,
} from '../../states/homeTab.atom';
import type { HomeTabItem } from 'shared/src/api';
import { HomeAtomic } from '../HomeAtomic';
import { FilterScopeProvider } from 'shared/src/component';
import { HomeWebView } from '../HomeWebView';
import { TodayAtomic } from '../TodayAtomic';
import { GoodsRanking } from '../GoodsRanking';
import { MarketRanking } from '../MarketRanking';
import { HomeTabBar } from './components/HomeTabBar';
import { useLogTabNavigation } from '../../hooks/useLogTabNavigation';
import { useHomeRoute } from '../../../../hooks/useHomeRoute';
import { useHomeNavigation } from '../../../../hooks/useHomeNavigation';
import { isWeb } from 'shared/src/utils';

export type HomeTabRoute = {
  key: string;
  title: string;
} & HomeTabItem;

const SCENE_FADE_DURATION = 1000;

/**
 * 탭 콘텐츠를 좌우 슬라이드(pager) 대신 opacity 로 fade-in/out 전환한다.
 * (TabView 의 swipe/animation 을 끄고, 활성 씬만 페이드로 나타난다.)
 */
const FadeScene = ({
  focused,
  children,
}: {
  focused: boolean;
  children: ReactNode;
}) => {
  const focusedOnce = useRef(false);

  const opacity = useSharedValue(focused ? 1 : 0);

  useEffect(() => {
    if (!focused) return;

    if (focusedOnce.current) return;

    opacity.value = withTiming(
      focused ? 1 : 0,
      {
        duration: SCENE_FADE_DURATION,
      },
      success => {
        if (success) {
          focusedOnce.current = true;
        }
      },
    );
  }, [focused, opacity, focusedOnce]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [{ translateY: interpolate(opacity.value, [0, 1], [10, 0]) }],
  }));

  return (
    <Animated.View style={[{ flex: 1 }, animatedStyle]}>
      {children}
    </Animated.View>
  );
};

const renderTabSceneContent = (route: HomeTabRoute): ReactNode => {
  if (route.type === 'ATOMIC') {
    return (
      <HomeAtomic
        tabKey={route.key}
        screenName={route.screen_name}
        nextToken={route.default_query?.next_token}
      />
    );
  }

  if (route.type === 'WEBVIEW') {
    return <HomeWebView url={route.default_query?.url} />;
  }

  if (route.type === 'CUSTOM') {
    if (route.screen_name === 'TODAY') {
      return <TodayAtomic screenName={route.screen_name} />;
    }

    if (route.screen_name === 'GOODS_RANKING') {
      return <GoodsRanking />;
    }

    if (route.screen_name === 'MARKET_RANKING') {
      return <MarketRanking />;
    }
  }

  return <Typography>{route.title}</Typography>;
};

/**
 * 탭마다 필터 스코프를 분리한다.
 *
 * 홈 탭들은 하나의 react-navigation route 를 공유하는 TabView 씬이라 instanceId 가 전부
 * 같다. 그래서 filterListAtom 슬롯도 공유되어 한 탭에서 적용한 필터가 다른 탭의
 * getScreens 요청/queryKey 까지 따라갔다.
 * (instanceId 를 바꾸지 않는 이유는 FilterScopeContext 주석 참고 — 네이티브 surface 식별자다.)
 *
 * 스코프 키는 `route.key`(= 탭 `id`)를 쓴다. `screen_name` 은 유일하지 않다 —
 * CATEGORY_DEPARTMENT 처럼 같은 screen_name 을 default_query 만 다르게 두 탭이 쓰는
 * 경우가 있어 그 둘이 다시 같은 슬롯을 공유한다. index 도 부적절하다: 서버가 탭 구성을
 * 내려주므로 순서/개수가 바뀌면 다른 탭의 필터 상태를 물려받는다.
 */
const renderTabScene = (route: HomeTabRoute): ReactNode => (
  <FilterScopeProvider scope={route.key}>
    {renderTabSceneContent(route)}
  </FilterScopeProvider>
);

export const HomeContents = () => {
  const { data: homeTabs } = useHomeTabs();
  const route = useHomeRoute<'Home'>();
  const navigation = useHomeNavigation<'Home'>();
  const size = useScreenSize();
  const setActiveHomeTab = useSetAtom(activeHomeTabAtom);

  const routes = useMemo<HomeTabRoute[]>(() => {
    return (
      homeTabs.item_list?.map(item => ({
        ...item,
        key: item.id,
        title: item.name,
      })) ?? []
    );
  }, [homeTabs.item_list]);

  const selectedRaw = route.params?.selected;
  const index = useMemo(() => {
    // selectedRaw가 있으면 id 또는 screen_name 매칭 우선
    if (selectedRaw != null) {
      const matched = routes.findIndex(
        item => item.id === selectedRaw || item.screen_name === selectedRaw,
      );
      if (matched >= 0) return matched;
      // 매칭 실패 시 default selected_id로 폴백
    }
    const fallback = routes.findIndex(item => item.id === homeTabs.selected_id);
    return fallback >= 0 ? fallback : 0;
  }, [routes, selectedRaw, homeTabs.selected_id]);

  // selectedRaw가 routes에 없으면 invalid param이라 정리
  useEffect(() => {
    if (selectedRaw == null) return;
    const matched = routes.some(
      item => item.id === selectedRaw || item.screen_name === selectedRaw,
    );
    if (!matched) {
      navigation.setParams({ selected: undefined });
    }
  }, [selectedRaw, routes, navigation]);

  // 현재 index + routes로부터 활성 탭 메타 도출 → atom 동기화
  const activeTab = useMemo<ActiveHomeTab | null>(() => {
    const current = routes[index];
    if (!current) return null;
    return {
      id: current.id,
      screen_name: current.screen_name,
      type: current.type,
      name: current.name,
    };
  }, [routes, index]);

  useEffect(() => {
    setActiveHomeTab(activeTab);
  }, [activeTab, setActiveHomeTab]);

  const indexRef = useRef(index);
  indexRef.current = index;
  const isTabClickRef = useRef(false);
  const { logClickUpperTab, logSwipeTabNavigation } =
    useLogTabNavigation(routes);

  const handleTabClick = useCallback(
    (nextIndex: number) => {
      isTabClickRef.current = true;
      logClickUpperTab(indexRef.current, nextIndex);
    },
    [logClickUpperTab],
  );

  const handleIndexChange = useCallback(
    (nextIndex: number) => {
      if (!isTabClickRef.current) {
        logSwipeTabNavigation(indexRef.current, nextIndex);
      }
      isTabClickRef.current = false;
      const nextId = routes[nextIndex]?.id;
      if (nextId) {
        navigation.setParams({ selected: nextId });
      }
    },
    [logSwipeTabNavigation, navigation, routes],
  );

  if (homeTabs.item_list.length === 0) return null;

  return (
    <TabView<HomeTabRoute>
      lazy={true}
      navigationState={{ index, routes }}
      lazyPreloadDistance={0}
      renderScene={item => {
        const scene = renderTabScene(item.route);
        // fade 전환은 웹에서만 적용. 네이티브는 씬을 그대로 렌더한다.
        if (!isWeb) return scene;
        const focused = routes[index]?.key === item.route.key;
        return <FadeScene focused={focused}>{scene}</FadeScene>;
      }}
      onIndexChange={handleIndexChange}
      initialLayout={{
        width: size.width,
      }}
      // 좌우 스와이프/슬라이드 대신 fade 전환을 쓰므로 둘 다 끈다.
      swipeEnabled={!isWeb}
      animationEnabled={!isWeb}
      renderTabBar={props => (
        <HomeTabBar {...props} onTabClick={handleTabClick} />
      )}
    />
  );
};
